import styles from "./ControlledAirlineAutocomplete.module.scss";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { Autocomplete, AutocompleteProps } from "@airhelp/react";
import { FieldValues, useController, UseControllerProps } from "react-hook-form";
import { useAirlineSearch } from "../hooks/useAirlineSearch";
import { Airline } from "../types/intake-funnel";
import { handleFieldChange } from "./handleFieldChange";
import { useTranslation } from "react-i18next";
import { airlineFilter } from "v2/utils/airline-filter";

type ControlledAirlineAutocompleteProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> & Partial<AutocompleteProps<AirlineOption>>;

const DEBOUNCE_TIMEOUT = 300;

export function ControlledAirlineAutocomplete<T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  handleChange: customHandleChange,
  ...props
}: ControlledAirlineAutocompleteProps<T>) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, DEBOUNCE_TIMEOUT);
  const { airlines = [], isError, isFetching } = useAirlineSearch(debouncedSearchTerm);
  const airlinesOptions = useMemo(
    () =>
      airlineFilter(airlines).map((airline) => ({
        label: airlineToString(airline),
        value: airline,
      })),
    [airlines],
  );

  const { field } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
  });
  const showMenu = !!searchTerm && airlinesOptions;

  return (
    <Autocomplete<AirlineOption>
      {...props}
      name={field.name}
      menuIsOpen={!!showMenu}
      options={airlinesOptions}
      filterOption={() => true}
      formatOptionLabel={(airline) => airlineToString(airline.value)}
      isLoading={isFetching}
      onInputChange={setSearchTerm}
      defaultValue={
        field.value ? { label: airlineToString(field.value), value: field.value } : null
      }
      handleChange={(option) => {
        handleFieldChange(field, option?.value || "");
        customHandleChange && customHandleChange(option);
      }}
      formControlStyles={{
        zIndex: 99,
      }}
      isInvalid={isError}
      colorScheme="warning"
      errorMessage={t("components.airline_autocomplete.search_error")}
      noOptionText={t("steps.itinerary_details.no_results_found")}
      className={styles.airlineAutocomplete}
    />
  );
}

function airlineToString(airline: Airline | undefined): string {
  return (
    airline?.displayName || `${airline?.name} (${airline?.iata || airline?.icao || airline?.code})`
  );
}

type AirlineOption = {
  label: string;
  value: Airline;
};
